/* =====================================================================
   Barista Fou 2026 — landing page behaviour
   Loaded by every bfou-*.liquid section; runs once per page and re-binds
   when a section is re-rendered in the theme editor.
   ===================================================================== */
(() => {
  if (window.BFOU2026) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const num = (v, fallback) => { const n = parseFloat(v); return Number.isFinite(n) ? n : fallback; };

  /* ---------------------------------------------------------------
     Confetti (palette colours, small canvas overlay)
     --------------------------------------------------------------- */
  const COLORS = ['#eb6135', '#d0c35f', '#9fbdfe', '#f7eae1', '#620e28'];
  let canvas, ctx, parts = [], running = false;
  function ensureCanvas() {
    if (canvas) return;
    canvas = document.createElement('canvas');
    canvas.className = 'bfou-confetti';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(canvas);
    ctx = canvas.getContext('2d');
    const size = () => { canvas.width = innerWidth * devicePixelRatio; canvas.height = innerHeight * devicePixelRatio; };
    size();
    addEventListener('resize', size);
  }
  // Big bursts (promos revealed, newsletter signup), and every burst on phones, start from the middle
  // of the screen: on a phone the stickers sit at the screen's edges, so confetti from there flies
  // straight off screen. On desktop a small burst (sticker tap) starts from the element, kept on
  // screen. Speeds scale with the screen so the confetti fills a phone as well as a desktop.
  function burst(el, n = 140, fromCentre = n >= 70 || innerWidth <= 760) {
    if (reduceMotion || !el) return;
    ensureCanvas();
    const r = el.getBoundingClientRect();
    const ox = fromCentre ? innerWidth / 2 : clamp(r.left + r.width / 2, 20, innerWidth - 20);
    const oy = fromCentre ? innerHeight * .55 : clamp(r.top + Math.min(r.height / 2, 120), 60, innerHeight * .8);
    const sx = clamp(innerWidth / 900, .45, 1);              // horizontal spread
    const sy = clamp(Math.sqrt(innerHeight / 800), .8, 1.1); // height reached
    const scale = fromCentre ? 1 : .7;
    for (let i = 0; i < n; i++) {
      const a = -Math.PI / 2 + (Math.random() - .5) * Math.PI * 1.3, v = (6 + Math.random() * 9) * scale;
      parts.push({ x: ox, y: oy, vx: Math.cos(a) * v * sx, vy: Math.sin(a) * v * sy, w: 4 + Math.random() * 4, h: 10 + Math.random() * 8,
        rot: Math.random() * 6, vr: (Math.random() - .5) * .3, c: COLORS[i % COLORS.length], life: 0 });
    }
    if (!running) { running = true; requestAnimationFrame(step); }
  }
  function step() {
    ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
    ctx.clearRect(0, 0, innerWidth, innerHeight);
    parts = parts.filter(p => p.life < 160 && p.y < innerHeight + 40);
    parts.forEach(p => {
      p.life++; p.vy += .28; p.vx *= .985; p.x += p.vx; p.y += p.vy; p.rot += p.vr;
      ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot); ctx.globalAlpha = Math.min(1, (160 - p.life) / 40);
      ctx.fillStyle = p.c; ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h); ctx.restore();
    });
    if (parts.length) requestAnimationFrame(step);
    else { running = false; ctx.clearRect(0, 0, innerWidth, innerHeight); }
  }

  /* ---------------------------------------------------------------
     Scroll-driven motion: hero layers, menu layers, floating icons, drink fill

     Every moving element gets a scroll range [a, b] (in px of page scroll) and the values at both
     ends. Ranges are measured only when the layout changes (load, fonts, images, width change,
     editor reloads), never while scrolling, from offsetTop (which ignores transforms).
     - Browsers with scroll-driven animations (Safari 26+, Chrome 115+): the element gets
       .bfou-sda and CSS runs the motion on the compositor, perfectly in step with the scroll.
     - Others: update() interpolates from window.scrollY only, with no layout reads per frame.
     The viewport height used is the small one (100svh, Safari's toolbar showing) and it only changes
     with the width, so Safari's toolbar collapsing during a scroll doesn't make anything jump.
     --------------------------------------------------------------- */
  const SDA = !!(window.CSS && CSS.supports && CSS.supports('animation-timeline: scroll()') && CSS.supports('animation-range: 0px 1px'));
  let items = [];          // { el, mode: page|view|fill|full, a, b, y0, y1, r0, r1, end }
  let vh = innerHeight;    // stable viewport height (svh)
  let lastWidth = innerWidth;
  let probe;

  function stableViewportHeight() {
    if (!probe) {
      probe = document.createElement('div');
      probe.setAttribute('aria-hidden', 'true');
      probe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:100vh;height:100svh;visibility:hidden;pointer-events:none';
      document.body.appendChild(probe);
    }
    return probe.offsetHeight || innerHeight;
  }

  // page position ignoring transforms
  function docTop(el) {
    let y = 0;
    for (let n = el; n; n = n.offsetParent) y += n.offsetTop;
    return y;
  }

  // HERO SIZE: on phones and short screens, give the artwork exactly the room left once the text
  // and buttons fit above the fold, measured on the real device (header, announcement, wrapped text).
  function sizeHeroes() {
    document.querySelectorAll('[data-bfou-hero]').forEach(hero => {
      if (!hero.classList.contains('bfou-hero--full')) { hero.style.removeProperty('--w'); return; }
      const art = hero.querySelector('.bfou-hero__art');
      const info = hero.querySelector('[data-bfou-hero-info]');
      const stack = hero.querySelector('.bfou-btn-stack');
      const wrap = hero.querySelector('.bfou-wrap');
      if (!art || !info) return;
      const mobile = matchMedia('(max-width: 760px)').matches;
      const cs = getComputedStyle(hero);
      const px = (v) => parseFloat(v) || 0;
      const ratio = 933 / 1380;                       // artwork height / width
      const hang = mobile ? .26 : 0;                  // stickers hang below the art on phones (× width)
      const nonArt = px(cs.paddingTop) + px(cs.paddingBottom)
        + info.offsetHeight + (mobile ? 8 : px(getComputedStyle(info).marginTop))
        + (stack ? stack.offsetHeight + px(getComputedStyle(stack).marginTop) : 0);
      const available = vh - (hero.getBoundingClientRect().top + window.scrollY) - nonArt;
      const maxW = mobile ? Math.min(innerWidth * 1.28, 620) : Math.min(820, wrap.clientWidth);
      // phones: never narrower than 106% of the screen, so the artwork always bleeds off both sides
      const minW = mobile ? innerWidth * 1.01 : Math.min(420, wrap.clientWidth);
      const w = clamp(available / (ratio + hang), minW, maxW);
      hero.style.setProperty('--w', `${w.toFixed(1)}px`);
    });
  }

  function measure() {
    vh = stableViewportHeight();
    sizeHeroes();
    items = [];
    const maxScroll = Math.max(0, document.documentElement.scrollHeight - vh);

    if (!reduceMotion) {
      // HERO (mode 'page'): layers rise at their own on-screen speed (65 = 65% of the scroll speed)
      // during the first screen of scrolling, i.e. they drift down by (1 - speed) × scroll.
      document.querySelectorAll('[data-bfou-hero]').forEach(hero => {
        const top = docTop(hero);
        const info = hero.querySelector('[data-bfou-hero-info]');
        hero.querySelectorAll('[data-bfou-layer]').forEach(el => {
          const drift = 1 - clamp(num(el.dataset.speed, 100), 0, 100) / 100;
          let b = top + vh, travel = vh * drift;
          const stickers = el.hasAttribute('data-bfou-stop-above-info') ? [...el.querySelectorAll('.bfou-stk')] : [];
          if (stickers.length && info && drift > 0) {
            // stickers stop 10px above the "4e édition" line
            const lowest = Math.max(...stickers.map(st => docTop(st) + st.offsetHeight));
            const cap = Math.max(0, docTop(info) - 10 - lowest);
            if (cap < travel) { travel = cap; b = top + cap / drift; }
          }
          items.push({ el, mode: 'page', a: top, b: Math.max(b, top + 1), y0: 0, y1: travel, r0: 0, r1: 0 });
        });
      });

      // Further down (mode 'view'): the motion follows the subject's trip across the screen, from its
      // top touching the bottom edge to its bottom touching the top edge. Offset = distance from the
      // screen centre × speed, so everything is in place when the subject is centred.
      const view = (el, subject, k, spin) => {
        const h = subject.offsetHeight, top = docTop(subject), A = (vh + h) / 2;
        items.push({ el, mode: 'view', a: top - vh, b: top + h, y0: A * k, y1: -A * k, r0: A * spin, r1: -A * spin });
      };
      // MENU TITLE: both layers follow the title itself
      document.querySelectorAll('[data-bfou-menu-title]').forEach(title => {
        title.querySelectorAll('[data-bfou-layer]').forEach(el => view(el, title, num(el.dataset.rise, 0) / 100, 0));
      });
      // FLOATING ICONS: drift and turn gently as they cross the screen
      document.querySelectorAll('[data-bfou-drift]').forEach(el => {
        if (el.offsetParent === null) return;   // inside the hidden countdown/promos state
        view(el, el, num(el.dataset.speed, 0), num(el.dataset.spin, 0));
      });
    }

    // DRINKS: empty when the word's top enters at the bottom of the screen, completely full once its
    // middle reaches data-full % of the screen height measured from the bottom (0 = full when it
    // appears, 100 = full only at the very top). If the page can't scroll that far, it finishes
    // filling where scrolling stops.
    document.querySelectorAll('[data-bfou-drinks].is-filling .bfou-drink').forEach(el => {
      const full = clamp(num(el.dataset.full, 33), 0, 100) / 100;
      const h = el.offsetHeight, a = docTop(el) - vh;
      const end = Math.max(1, Math.min(h / 2 + vh * full, maxScroll - a));
      items.push({ el, mode: full === 0 ? 'full' : 'fill', a, b: a + end, end });
    });

    const restart = [];
    items.forEach(it => {
      const st = it.el.style;
      st.removeProperty('transform');
      it.el.classList.remove('bfou-sda', 'bfou-sda-view');
      if (it.mode === 'full') { st.setProperty('--fill', '1'); return; }
      if (it.mode === 'fill') {
        st.removeProperty('--fill');
        st.setProperty('--bfou-end', `${it.end.toFixed(1)}px`);
      } else {
        st.setProperty('--bfou-a', `${it.a.toFixed(1)}px`);
        st.setProperty('--bfou-b', `${it.b.toFixed(1)}px`);
        st.setProperty('--bfou-y0', `${it.y0.toFixed(1)}px`);
        st.setProperty('--bfou-y1', `${it.y1.toFixed(1)}px`);
        st.setProperty('--bfou-r0', `${it.r0.toFixed(2)}deg`);
        st.setProperty('--bfou-r1', `${it.r1.toFixed(2)}deg`);
      }
      if (SDA) { it.el.classList.add(it.mode === 'page' ? 'bfou-sda' : 'bfou-sda-view'); restart.push(it.el); }
    });
    // Safari keeps the values a running scroll animation started with: restart them so new
    // measurements (fonts, images, width change) always take effect
    restart.forEach(el => { el.style.animation = 'none'; });
    if (restart.length) void document.body.offsetWidth;
    restart.forEach(el => { el.style.removeProperty('animation'); });
    update();
  }

  function update() {
    if (SDA) return;
    const y = window.scrollY;
    for (const it of items) {
      if (it.mode === 'full') continue;
      const t = clamp((y - it.a) / (it.b - it.a), 0, 1);
      if (it.mode === 'fill') { it.el.style.setProperty('--fill', t.toFixed(3)); continue; }
      const ty = it.y0 + (it.y1 - it.y0) * t, rot = it.r0 + (it.r1 - it.r0) * t;
      it.el.style.transform = `translate3d(0, ${ty.toFixed(1)}px, 0)${rot ? ` rotate(${rot.toFixed(2)}deg)` : ''}`;
    }
  }

  function collect() {
    document.querySelectorAll('[data-bfou-drinks]').forEach(g => g.classList.toggle('is-filling', g.dataset.fill === 'true' && !reduceMotion));
    remeasure();
  }

  let queued = false, measureQueued = false;
  const queue = () => { if (!queued && !SDA) { queued = true; requestAnimationFrame(() => { queued = false; update(); }); } };
  const remeasure = () => { if (!measureQueued) { measureQueued = true; requestAnimationFrame(() => { measureQueued = false; measure(); }); } };

  /* ---------------------------------------------------------------
     Countdown → promos
     --------------------------------------------------------------- */
  const timers = new WeakMap();

  function initCountdown(root) {
    if (timers.has(root)) clearInterval(timers.get(root));
    const end = Date.parse(root.dataset.end);
    const preview = root.dataset.preview || 'auto';   // only output in the theme editor
    const countdown = root.querySelector('[data-state="countdown"]');
    const promos = root.querySelector('[data-state="promos"]');
    const digits = {};
    root.querySelectorAll('[data-unit]').forEach(el => { digits[el.dataset.unit] = el; });
    let first = true;

    const setDigit = (el, v) => {
      if (!el) return;
      const s = String(v).padStart(2, '0');
      if (el.textContent === s) return;
      el.textContent = s;
      if (!reduceMotion && !first) { el.classList.remove('is-flipping'); void el.offsetWidth; el.classList.add('is-flipping'); }
    };
    const show = (which, celebrate) => {
      const next = which === 'promos' ? promos : countdown;
      const prev = which === 'promos' ? countdown : promos;
      if (!next || !prev || !next.hidden) return;
      const swap = () => {
        prev.hidden = true; prev.classList.remove('is-leaving');
        next.hidden = false; next.classList.remove('is-entering');
        if (!first) { void next.offsetWidth; next.classList.add('is-entering'); }
        if (celebrate) burst(root);
        collect();
      };
      if (first || reduceMotion) swap();
      else { prev.classList.add('is-leaving'); setTimeout(swap, 350); }
    };
    const tick = () => {
      const left = Number.isFinite(end) ? Math.max(0, end - Date.now()) : 0;
      setDigit(digits.d, Math.floor(left / 864e5));
      setDigit(digits.h, Math.floor(left / 36e5) % 24);
      setDigit(digits.m, Math.floor(left / 6e4) % 60);
      setDigit(digits.s, Math.floor(left / 1e3) % 60);
      if (preview === 'countdown') show('countdown');
      else if (preview === 'promos') show('promos');
      else show(left <= 0 ? 'promos' : 'countdown', left <= 0 && !first);
      first = false;
    };
    tick();
    timers.set(root, setInterval(tick, 1000));
  }

  /* ---------------------------------------------------------------
     Newsletter → Klaviyo Client Subscriptions API (public key only, safe in the browser)
     Klaviyo supports each API revision for two years: 2026-10-15 is supported until 2028-10-15.
     After that, Klaviyo answers 400 invalid_revision and nobody can sign up: move to a newer revision.
     --------------------------------------------------------------- */
  function initNewsletter(form) {
    if (form.dataset.bound) return;
    form.dataset.bound = 'true';
    const input = form.querySelector('input[type="email"]');
    const button = form.querySelector('button[type="submit"]');
    const msg = form.querySelector('[data-bfou-msg]');
    const d = form.dataset;

    const subscribe = (email) => {
      if (!d.company || !d.list) return Promise.reject(new Error('missing-keys'));
      return fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(d.company)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', 'revision': '2026-10-15' },
        body: JSON.stringify({ data: { type: 'subscription', attributes: {
          custom_source: d.source || 'Barista Fou 2026',
          profile: { data: { type: 'profile', attributes: { email } } }
        }, relationships: { list: { data: { type: 'list', id: d.list } } } } })
      }).then(r => { if (!r.ok) throw new Error(String(r.status)); });
    };

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const email = input.value.trim();
      const valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
      input.setAttribute('aria-invalid', String(!valid));
      msg.classList.remove('is-success');
      if (!valid) { msg.textContent = d.msgInvalid; input.focus(); return; }
      button.disabled = true;
      msg.textContent = d.msgSending;
      subscribe(email).then(() => {
        msg.classList.add('is-success');
        msg.textContent = d.msgSuccess;
        input.value = '';
        burst(button, 70);
      }).catch((err) => {
        msg.textContent = d.msgError;
        if (err && err.message === 'missing-keys') console.warn('[Barista Fou] Add the Klaviyo public API key and list ID in the section settings.');
      }).finally(() => { button.disabled = false; });
    });
  }

  /* ---------------------------------------------------------------
     Small touches: sticker wiggle, recap video, line-up reveal
     --------------------------------------------------------------- */
  function initStickers(scope) {
    scope.querySelectorAll('.bfou-stk:not([data-bound])').forEach(s => {
      s.dataset.bound = 'true';
      s.addEventListener('click', () => { s.classList.remove('is-wiggling'); void s.offsetWidth; s.classList.add('is-wiggling'); burst(s, innerWidth <= 760 ? 60 : 40); });
    });
  }

  function initRecap(scope) {
    scope.querySelectorAll('[data-bfou-recap]:not([data-bound])').forEach(media => {
      media.dataset.bound = 'true';
      const video = media.querySelector('video');
      const play = media.querySelector('.bfou-recap__play');
      if (!video || !play) return;
      play.addEventListener('click', () => {
        video.controls = true;
        video.play().then(() => media.classList.add('is-playing')).catch(() => media.classList.add('is-playing'));
      });
      video.addEventListener('playing', () => media.classList.add('is-playing'));
    });
  }

  let revealObserver;
  function initReveal(scope) {
    if (reduceMotion || !('IntersectionObserver' in window)) return;
    revealObserver = revealObserver || new IntersectionObserver(entries => entries.forEach(e => {
      if (!e.isIntersecting) return;
      e.target.animate([{ transform: 'translateY(18px)', opacity: .35 }, { transform: 'none', opacity: 1 }],
        { duration: 600, easing: 'cubic-bezier(.34,1.56,.64,1)', delay: num(e.target.dataset.i, 0) * 70 });
      revealObserver.unobserve(e.target);
    }), { threshold: .3 });
    scope.querySelectorAll('[data-bfou-reveal]').forEach((el, i) => {
      el.dataset.i = i % 8;
      if (el.getBoundingClientRect().top > innerHeight) revealObserver.observe(el); // only items below the fold
    });
  }

  /* ---------------------------------------------------------------
     Boot + theme editor support
     --------------------------------------------------------------- */
  function init(scope = document) {
    scope.querySelectorAll('[data-bfou-countdown]').forEach(initCountdown);
    scope.querySelectorAll('[data-bfou-klaviyo]').forEach(initNewsletter);
    initStickers(scope);
    initRecap(scope);
    initReveal(scope);
    collect();
  }

  window.BFOU2026 = { init, burst };
  addEventListener('scroll', queue, { passive: true });
  // Safari's toolbar collapsing fires resize with the same width: ignore it, nothing depends on it
  addEventListener('resize', () => { if (innerWidth !== lastWidth) { lastWidth = innerWidth; remeasure(); } });
  addEventListener('load', remeasure);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(remeasure);
  if ('ResizeObserver' in window) {
    let lastHeight = 0;
    new ResizeObserver(() => {
      const h = document.documentElement.scrollHeight;
      if (Math.abs(h - lastHeight) > 1) { lastHeight = h; remeasure(); }
    }).observe(document.documentElement);
  }
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-bfou-countdown]').forEach(r => { if (timers.has(r)) clearInterval(timers.get(r)); });
    setTimeout(collect, 0);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
